/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Flex, Text, Stack, Pressable, Box } from '@atlaskit/primitives/compiled';
import { cssMap, jsx, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { expValEquals } from '@confluence/feature-experiments';

const i18n = defineMessages({
	usedBefore: {
		id: 'experiment-personalized-templates-v2.template-list.template-card.used-before',
		defaultMessage:
			'{count, plural, one {Used {count} time before} other {Used {count} times before}}',
		description:
			'Text that appears on hover in a card to indicate how many times this template card has been used before',
	},
	notUsedYet: {
		id: 'experiment-personalized-templates-v2.template-list.template-card.havent-used-before',
		defaultMessage: 'Not used yet',
		description:
			'Text that appears on hover in a card to indicate that the user has never used this template before',
	},
});

const styles = cssMap({
	borderStyles: {
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
	},
	containerStyles: {
		height: '160px',
		backgroundColor: token('elevation.surface.sunken'),
		paddingLeft: token('space.200'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		boxSizing: 'border-box',
		overflow: 'hidden',

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	contentContainerStyles: {
		height: '100%',
		textAlign: 'start',
		gap: token('space.200'),
	},
	imageContainerStyles: {
		height: '92px',
		width: '100%',
		borderWidth: 0,
		borderTopLeftRadius: token('radius.small'),
		borderTopRightRadius: token('radius.small'),
		overflow: 'hidden',
	},
	pressableStyles: {
		width: '100%',
		height: '100%',
		padding: token('space.0'),
		margin: token('space.0'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.large'),
		overflow: 'hidden',
	},
	iconContainerStyles: {
		padding: token('space.100'),
		borderWidth: token('border.width'),
		borderRadius: token('radius.small'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
	},
	headerStyles: {
		alignItems: 'center',
		gap: token('space.150'),
	},
});

export type PTV2TemplateCardProps = {
	title: string;
	subTitle: string;
	onClick: () => void;
	icon: React.ReactNode;
	image: React.ReactNode;
	usage: number;
};

export const PTV2TemplateCard = ({
	title,
	subTitle,
	onClick,
	icon,
	image,
	usage,
}: PTV2TemplateCardProps): JSX.Element => {
	const [isHovered, setIsHovered] = useState<boolean>(false);
	const { formatMessage } = useIntl();

	const rernderedSubTitle =
		usage && isHovered ? formatMessage(i18n.usedBefore, { count: usage }) : subTitle;
	return (
		<Pressable
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			testId="ptv2-template-card-button"
			xcss={cx(styles.pressableStyles, styles.borderStyles)}
			onClick={onClick}
			// Note: expValEquals must be inlined at each callsite to satisfy the
			// `eslint-plugin-check-bifrost` rule which requires statically analyzable
			// experiment usages. The cc_activation_a11y_fixes experiment exposes the
			// card to assistive tech as a link (see ACTIVATION-2644 / A11Y-42432).
			// tabIndex is intentionally omitted because the underlying <button>
			// element is already focusable with tabindex=0 by default.
			role={expValEquals('cc_activation_a11y_fixes', 'isEnabled', true) ? 'link' : undefined}
		>
			<div css={[styles.containerStyles]}>
				<Flex xcss={styles.contentContainerStyles} direction="column">
					<Flex xcss={styles.headerStyles}>
						<Box xcss={styles.iconContainerStyles}>{icon}</Box>
						<Stack>
							<Text maxLines={1} weight="bold">
								{title}
							</Text>
							<Text maxLines={1} color="color.text.subtlest" size="small">
								{rernderedSubTitle}
							</Text>
						</Stack>
					</Flex>
					<div css={[styles.imageContainerStyles]}>{image}</div>
				</Flex>
			</div>
		</Pressable>
	);
};
